import React from 'react';
import { Dialog } from 'primereact/dialog';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import type { MiAlcance, VistaTienda } from '../types/metas.types';
import { nombrePropioSiMayusculas } from '../utils/autorizacion';
import { pasoPorGerente } from '../utils/captura';
import { etiquetaSemana } from '../utils/semanas';
import { instanteMx, nombrePropio } from '../utils/formato';
import { ColaboradoresTabla } from './ColaboradoresTabla';
import { CLASE_OVERLAY } from './ui/clases';
import { EstadoPill } from './ui/Pill';

interface Props {
  /** La vista que ya cargó el detalle: no se vuelve a pedir a BUK ni al ERP. */
  vista: VistaTienda | null;
  alcance: MiAlcance;
  onHide: () => void;
}

/**
 * «Ver captura completa» para quien NO tiene la pestaña Captura (un regional
 * puro) o no puede abrir esa tienda en ella: la tabla completa de la captura,
 * de sólo lectura, con todas sus columnas (antigüedad, venta promedio, semana
 * anterior, apoyos, y desde el 21-oct-2026 transporte, bodeguero fijo e
 * incapacidad por persona). Usa la vista del detalle, así que no cuesta otra
 * plantilla. Si la capturó el gerente, quién y cuándo, su nota y, bajo cada meta
 * que cambió el supervisor, «El gerente puso $X».
 */
export const CapturaLecturaDialog: React.FC<Props> = ({ vista, alcance, onHide }) => {
  const esMovil = useEsMovil(767);
  return (
    <Dialog
      // La ✕ en español: el locale global de PrimeReact sigue en `en` («Close»).
      ariaCloseIconLabel="Cerrar"
      className={CLASE_OVERLAY}
      header={
        vista ? (
          <span className="flex flex-wrap items-center gap-3">
            <span>
              {vista.tienda.chain} {vista.tienda.num} · {vista.tienda.nombre} — {etiquetaSemana(vista.semana.lunes)}
            </span>
            <EstadoPill estado={vista.estado.estado} />
          </span>
        ) : (
          ''
        )
      }
      visible={!!vista}
      onHide={onHide}
      maximized={esMovil}
      onMaximize={() => undefined}
      style={esMovil ? { width: '100vw', maxWidth: '100vw', margin: 0 } : { width: '95vw', maxWidth: 1200 }}
      modal
    >
      {vista && (
        <div className="flex flex-col gap-3" data-testid="captura-lectura">
          <p className="m-0 text-sm text-metas-texto-2">
            Supervisor {nombrePropio(vista.tienda.supervisor)} · Regional {nombrePropio(vista.tienda.regional)} · sólo lectura
          </p>
          {pasoPorGerente(vista.estado) && (
            <p className="m-0 text-sm text-metas-texto-2 [overflow-wrap:anywhere]" data-testid="lectura-gerente">
              Capturó {vista.estado.enviadoGerenteNombre ? `${nombrePropioSiMayusculas(vista.estado.enviadoGerenteNombre)} (gerente)` : 'el gerente'}
              {vista.estado.enviadoGerenteEn ? ` el ${instanteMx(vista.estado.enviadoGerenteEn)}` : ''}
              {vista.estado.notaGerente ? ` · nota del gerente: «${vista.estado.notaGerente}»` : ''}
            </p>
          )}
          <ColaboradoresTabla
            filas={vista.colaboradores}
            tienda={{ chain: vista.tienda.chain, num: vista.tienda.num }}
            motivos={alcance.motivos}
            motivosHistoricos={alcance.motivosHistoricos}
            soloLectura
            referencia={vista.referencia}
            conGerente={pasoPorGerente(vista.estado)}
          />
          {vista.estado.notaSupervisor && (
            <p className="m-0 text-sm text-metas-texto-2 [overflow-wrap:anywhere]">
              Nota del supervisor: «{vista.estado.notaSupervisor}»
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default CapturaLecturaDialog;
